import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  Grid,
  IconButton,
  makeStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Cancel, History } from '@material-ui/icons';
import { AxiosError } from 'axios';
import moment from 'moment';
import useError from 'components/ErrorProvider/useError';
import { ITechnicalRoomHistory } from '../interfaces';
import { loadTechnicalRoomHistoryItem } from '../api';
import { TechnicalRoomHistoryItemDialog } from '../components';

const useStyles = makeStyles({
  table: {
    '& thead': {
      '& th': {
        fontWeight: 600,
        backgroundColor: '#FDD7E7',
      },
    },

    '& tbody': {
      '& tr': {
        '& td': {
          backgroundColor: '#FAFAFA',
          transition: 'background-color 200ms',
        },
        '&:hover': {
          '& td': {
            backgroundColor: '#f5f5f5',
          },
        },
      },
    },
  },
  loader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    zIndex: 1000,
  },
});

interface IProps {
  disabled: boolean;
  loading: boolean;
  technicalRoomHistoryList: ITechnicalRoomHistory[];
  onClose(): void;
}

const TechnicalRoomHistoryList: React.FC<IProps & DialogProps> = ({
  open,
  technicalRoomHistoryList,
  loading,
  disabled,
  onClose,
}) => {
  // Local state
  const [currentTechnicalRoomHistoryItem, setCurrentTechnicalRoomHistoryItem] = useState<ITechnicalRoomHistory | null>(
    null,
  );
  const [technicalRoomHistoryItemDialogOpened, setOpenedTechnicalRoomHistoryItemDialog] = useState(false);
  const [technicalRoomHistoryItemLoading, setTechnicalRoomHistoryItemLoading] = useState(false);

  // Styles
  const classes = useStyles();

  // Global notification
  const { addError } = useError();

  // Methods
  const handleLoadTechnicalRoomHistoryItem = async (technicalRoomHistory: ITechnicalRoomHistory) => {
    if (!technicalRoomHistory.technicalRoomHistoryId) return;
    handleOpenTechnicalRoomHistoryItemDialog();
    setTechnicalRoomHistoryItemLoading(true);

    try {
      setCurrentTechnicalRoomHistoryItem(
        await loadTechnicalRoomHistoryItem(technicalRoomHistory.technicalRoomHistoryId),
      );
    } catch (error) {
      addError((error as AxiosError).response!.data.message);
    } finally {
      setTechnicalRoomHistoryItemLoading(false);
    }
  };

  const handleOpenTechnicalRoomHistoryItemDialog = () => {
    setOpenedTechnicalRoomHistoryItemDialog(true);
    setCurrentTechnicalRoomHistoryItem(null);
  };

  // Main render
  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="body">
        <DialogTitle>
          <b>Информация о действиях</b>
        </DialogTitle>

        {loading && (
          <Box className={classes.loader}>
            <CircularProgress />
          </Box>
        )}

        <DialogContent dividers>
          <Grid container spacing={3}>
            {Boolean(technicalRoomHistoryList.length) ? (
              <Grid item xs={12}>
                <Typography component="h3" variant="h6" gutterBottom>
                  Основная информация
                </Typography>

                <TableContainer component={Paper} variant="outlined">
                  <Table className={classes.table}>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID пользователя</TableCell>
                        <TableCell>Пользователь</TableCell>
                        <TableCell>Тип действия</TableCell>
                        <TableCell>Дата и время</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {technicalRoomHistoryList.map((technicalRoomHistory) => {
                        const { createUserId, createUserName, logActionType, createTimestamp, technicalRoomHistoryId } =
                          technicalRoomHistory;

                        return (
                          <TableRow key={technicalRoomHistoryId}>
                            <TableCell>{createUserId}</TableCell>
                            <TableCell>{createUserName}</TableCell>
                            <TableCell>{logActionType}</TableCell>
                            <TableCell>{moment(createTimestamp).format('DD/MM/YYYY')}</TableCell>
                            <TableCell>
                              <Tooltip title="История изменений">
                                <IconButton
                                  size="small"
                                  aria-label="История изменений"
                                  onClick={() => handleLoadTechnicalRoomHistoryItem(technicalRoomHistory)}
                                >
                                  <History />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            ) : (
              <Grid item xs={12}>
                <Box sx={{ p: 2 }}>
                  <Typography align="center" variant="h6">
                    <b>Истории нет</b>
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant="contained" disabled={disabled} onClick={onClose} startIcon={<Cancel />}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      <TechnicalRoomHistoryItemDialog
        open={technicalRoomHistoryItemDialogOpened}
        loading={technicalRoomHistoryItemLoading}
        technicalRoomHistoryItem={currentTechnicalRoomHistoryItem}
        onClose={() => setOpenedTechnicalRoomHistoryItemDialog(false)}
      />
    </>
  );
};

export default TechnicalRoomHistoryList;
